"use client";

/**
 * sak's in-app settings dialog — the DEFAULT settings surface (settings-window
 * plan). A small modal over the app shell, styled with the served suite-ui
 * dialog classes and rendering the same shared sections the separate settings
 * window hosts, in the same two-column shape: the left nav picks the section,
 * the right column shows its content. Self-contained: mounted once at the app
 * root, it listens for OPEN_SETTINGS_EVENT (raised by openSettings' in-app
 * half) and manages its own open state.
 */

import { useEffect, useState } from "react";

import { SettingsAppearanceSection, SettingsGeneralSection } from "./sections";
import { ApiAccessSection, BillingSection, SecuritySection } from "./securitySections";
import { OPEN_SETTINGS_EVENT } from "./openSettings";
import { useTranslation } from "react-i18next";

/** The dialog's sections, in nav order — same surface as the settings window. */
const SECTIONS = [
  { key: "general", labelKey: "settings.general" },
  { key: "appearance", labelKey: "settings.appearance" },
  { key: "security", labelKey: "settings.security" },
  { key: "api", labelKey: "settings.apiAccess" },
  { key: "billing", labelKey: "settings.billing" },
] as const;

type SectionKey = (typeof SECTIONS)[number]["key"];

/** The active section's content, resolved from the nav pick. */
function ActiveSection({ tab, onClosePanel }: { tab: SectionKey; onClosePanel: () => void }) {
  if (tab === "appearance") return <SettingsAppearanceSection />;
  if (tab === "security") return <SecuritySection />;
  if (tab === "billing") return <BillingSection />;
  if (tab === "api") return <ApiAccessSection />;
  return <SettingsGeneralSection onClosePanel={onClosePanel} />;
}

export function SettingsDialog() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<SectionKey>("general");

  // The IN-APP settings dialog (the default path — settings-window plan).
  // Raised by openSettings' openInApp half through the window event; never
  // routed back through the dispatcher (that would loop). Every open starts
  // from the first section, like a fresh native dialog.
  useEffect(() => {
    const handleOpenSettings = (): void => {
      setTab("general");
      setOpen(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, handleOpenSettings);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, handleOpenSettings);
  }, []);

  // Native-dialog semantics: Escape closes while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="suite-ui-dialog-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={t("navigation.settings")}
      onClick={(event) => {
        if (event.target === event.currentTarget) setOpen(false);
      }}
    >
      <div className="suite-ui-dialog app-settings-dialog">
        <div className="suite-ui-dialog-header app-settings-dialog-header">
          <h3 className="app-settings-dialog-title">{t("navigation.settings")}</h3>
          <button
            type="button"
            className="app-settings-dialog-close"
            aria-label={t("common.close")}
            onClick={() => setOpen(false)}
          >
            ×
          </button>
        </div>
        <div className="suite-ui-dialog-body app-settings-dialog-columns">
          <nav className="app-settings-dialog-nav" aria-label={t("navigation.settings")}>
            {SECTIONS.map((section) => {
              const active = section.key === tab;
              return (
                <button
                  key={section.key}
                  type="button"
                  className={
                    active
                      ? "app-settings-dialog-nav-item is-active"
                      : "app-settings-dialog-nav-item"
                  }
                  aria-current={active ? "page" : undefined}
                  onClick={() => setTab(section.key)}
                >
                  {t(section.labelKey)}
                </button>
              );
            })}
          </nav>
          <div className="app-settings-dialog-content">
            <ActiveSection tab={tab} onClosePanel={() => setOpen(false)} />
          </div>
        </div>
      </div>
    </div>
  );
}
